// Phase 15a — Route Engine domain foundation (slice 1).
//
// MASTER §3 Phase 15 / §6: the Route Engine is a cross-cutting
// strategy-posture concept that will eventually shape Sika plans,
// ROLLOVER chains and slip construction. This slice is the PURE
// DOMAIN LAYER only — no UI, no wiring into ROLLOVER/ladder/singles,
// no I/O. It is additive: it does not touch the live /sika scenario
// panel (which still depends on the 3-route SikaScenarioRoute).
//
// Honesty framework is inherited verbatim from calculateSikaPlan:
// probability + downside shown for every route, "could reach" never
// "will reach", infeasible routes surfaced as such — see
// ../sika/sika-plan-calculator.ts.
//
// The 7 named routes come straight from MASTER §3 Phase 15. Three of
// them are the existing Sika postures (balanced ≡ the legacy Sika
// "moderate"); the other four are new. `multi_market` is defined for
// completeness but its real diversified-chain behaviour belongs to
// Phase 18 (multi-market expansion) — until then it plans as a single
// balanced chain and is flagged phase18Dependent.

import {
  calculateSikaPlan,
  type SikaPlanScenario,
  type SikaScenarioInput,
  type SikaScenarioRoute,
} from "../sika/sika-plan-calculator.js";

export type StrategyRoute =
  | "conservative"
  | "balanced"
  | "aggressive"
  | "recovery"
  | "protection"
  | "goal"
  | "multi_market";

export const STRATEGY_ROUTES: readonly StrategyRoute[] = [
  "conservative",
  "balanced",
  "aggressive",
  "recovery",
  "protection",
  "goal",
  "multi_market",
] as const;

export interface StrategyRouteProfile {
  readonly route: StrategyRoute;
  readonly label: string;
  /** One-line honest intent. Never a promise of outcome. */
  readonly intent: string;
  readonly targetOdds: number;
  readonly takeoutRate: number;
  readonly winProbabilityPerWeek: number;
  /** This slice is domain-only; nothing is wired beyond this module. */
  readonly wiringStatus: "domain_only";
  /** True ⇒ real behaviour is gated on Phase 18 (multi-market). */
  readonly phase18Dependent: boolean;
}

// Planning assumptions ONLY. Not historical performance, not a
// promise. The 3 existing postures reuse the exact Sika numbers so
// `balanced` is provably identical to the legacy Sika "moderate"
// (locked by a test). The 4 new postures are conservative-by-design
// planning priors, editable when real settled data exists:
//   recovery   — post-loss rebuild: low odds, high takeout, lock fast
//   protection — defend the lead: near-flat stake, max withdrawal
//   goal       — target-driven middle ground (conservative↔balanced)
//   multi_market — single balanced chain until Phase 18 splits it
export const STRATEGY_ROUTE_PROFILES: Record<
  StrategyRoute,
  StrategyRouteProfile
> = {
  conservative: {
    route: "conservative",
    label: "Conservative",
    intent: "Lowest target odds, steady protected growth.",
    targetOdds: 2.0,
    takeoutRate: 0.30,
    winProbabilityPerWeek: 0.48,
    wiringStatus: "domain_only",
    phase18Dependent: false,
  },
  balanced: {
    route: "balanced",
    label: "Balanced",
    intent: "Even trade-off between compounding and protection.",
    targetOdds: 2.5,
    takeoutRate: 0.40,
    winProbabilityPerWeek: 0.38,
    wiringStatus: "domain_only",
    phase18Dependent: false,
  },
  aggressive: {
    route: "aggressive",
    label: "Aggressive",
    intent: "Highest target odds, fastest growth, highest bust risk.",
    targetOdds: 3.0,
    takeoutRate: 0.30,
    winProbabilityPerWeek: 0.32,
    wiringStatus: "domain_only",
    phase18Dependent: false,
  },
  recovery: {
    route: "recovery",
    label: "Recovery",
    intent: "Rebuild after a loss: low odds, withdraw early and often.",
    targetOdds: 1.8,
    takeoutRate: 0.45,
    winProbabilityPerWeek: 0.55,
    wiringStatus: "domain_only",
    phase18Dependent: false,
  },
  protection: {
    route: "protection",
    label: "Protection",
    intent: "Defend the lead: near-flat stake, maximum withdrawal.",
    targetOdds: 1.7,
    takeoutRate: 0.50,
    winProbabilityPerWeek: 0.58,
    wiringStatus: "domain_only",
    phase18Dependent: false,
  },
  goal: {
    route: "goal",
    label: "Goal",
    intent: "Target-driven middle ground between conservative and balanced.",
    targetOdds: 2.2,
    takeoutRate: 0.35,
    winProbabilityPerWeek: 0.43,
    wiringStatus: "domain_only",
    phase18Dependent: false,
  },
  multi_market: {
    route: "multi_market",
    label: "Multi-market",
    intent:
      "Diversified posture. Plans as a single balanced chain until Phase 18 enables real multi-market splitting.",
    targetOdds: 2.5,
    takeoutRate: 0.40,
    winProbabilityPerWeek: 0.38,
    wiringStatus: "domain_only",
    phase18Dependent: true,
  },
};

// Phase 15g — posture → target-odds multiplier. `balanced` is the
// neutral baseline (1.0); every other posture scales the construction
// target so each user's pipeline output diverges (founder moat
// ruling). SINGLE SOURCE OF TRUTH: the ladder-pipeline edge function
// inlines an exact mirror of this map (it is standalone Deno and
// cannot import src/), kept honest by a parity test.
const BALANCED_BASELINE_ODDS = STRATEGY_ROUTE_PROFILES.balanced.targetOdds;

export const ROUTE_ODDS_MULTIPLIER: Record<StrategyRoute, number> =
  (Object.keys(STRATEGY_ROUTE_PROFILES) as StrategyRoute[]).reduce(
    (acc, r) => {
      acc[r] = Number(
        (STRATEGY_ROUTE_PROFILES[r].targetOdds / BALANCED_BASELINE_ODDS)
          .toFixed(4),
      );
      return acc;
    },
    {} as Record<StrategyRoute, number>,
  );

/** Neutral (1.0) for null/unknown ⇒ pipeline behaves exactly as
 *  before. Never throws on bad input (it crosses a trust boundary). */
export function routeOddsMultiplier(route: string | null | undefined): number {
  if (!route) return 1;
  const m = ROUTE_ODDS_MULTIPLIER[route as StrategyRoute];
  return typeof m === "number" ? m : 1;
}

/** The legacy Sika "moderate" posture is exactly Route-Engine "balanced". */
export function fromSikaScenarioRoute(r: SikaScenarioRoute): StrategyRoute {
  return r === "moderate" ? "balanced" : r;
}

/**
 * Map a StrategyRoute back to a SikaScenarioRoute, or null when the
 * route has no equivalent in the 3-posture Sika panel (recovery /
 * protection / goal / multi_market). Lets future wiring decide
 * whether a route is renderable by the existing Sika surface.
 */
export function toSikaScenarioRoute(
  r: StrategyRoute,
): SikaScenarioRoute | null {
  if (r === "balanced") return "moderate";
  if (r === "conservative" || r === "aggressive") return r;
  return null;
}

/** A plan carrying the full 7-route identity (existing Sika scenario
 *  shape, only the `route` field widened). The underlying scenario
 *  type is left untouched. */
export type StrategyRoutePlan =
  & Omit<SikaPlanScenario, "route">
  & { readonly route: StrategyRoute };

/**
 * Deterministic plan for any StrategyRoute. Pure delegation to the
 * existing, untouched calculateSikaPlan — the route's profile supplies
 * the assumptions; an explicit takeoutRate on the input still overrides
 * (parity with calculateSikaScenarios). No I/O, no promises.
 */
export function calculateStrategyRoutePlan(
  input: SikaScenarioInput,
  route: StrategyRoute,
): StrategyRoutePlan {
  const p = STRATEGY_ROUTE_PROFILES[route];
  const scenario = calculateSikaPlan(
    {
      targetAmount: input.targetAmount,
      currentAmount: input.currentAmount,
      openingStake: input.openingStake,
      maxWeeks: input.maxWeeks,
      targetOdds: p.targetOdds,
      takeoutRate: input.takeoutRate ?? p.takeoutRate,
      winProbabilityPerWeek: p.winProbabilityPerWeek,
    },
    // The base calculator only knows 3 route names; the value here is
    // irrelevant because we override `route` with the true 7-route
    // identity below.
    "moderate",
  );
  return { ...scenario, route };
}

/** Convenience: every route's plan for one input (parity with
 *  calculateSikaScenarios, widened to all 7 postures). */
export function calculateAllStrategyRoutePlans(
  input: SikaScenarioInput,
): Record<StrategyRoute, StrategyRoutePlan> {
  const out = {} as Record<StrategyRoute, StrategyRoutePlan>;
  for (const route of STRATEGY_ROUTES) {
    out[route] = calculateStrategyRoutePlan(input, route);
  }
  return out;
}
